import React, {useState, useEffect, useCallback} from 'react';
import {Button} from '../ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import {Input} from '../ui/input';
import {Label} from '../ui/label';
import {toast} from '../ui/use-toast';
import {Edit, Key, Eye, EyeOff, Image, Upload, X, Hash} from 'lucide-react';
import {useAuth} from '../../contexts/PhpAuthContext';
import {useHospitalAuth} from '../../contexts/HospitalAuthContext';
import {useHospitalBranding} from '../../contexts/HospitalBrandingContext';
import {apiRequest} from '../../config/api';

const AdminSettingsTab = () => {
  const {profile} = useAuth();
  const {hospital, refreshHospitalStatus} = useHospitalAuth();
  const {branding, refreshBranding} = useHospitalBranding();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [canReset, setCanReset] = useState(true);
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [prefixOverrideEnabled, setPrefixOverrideEnabled] = useState(false);
  const [prefixOverride, setPrefixOverride] = useState('');
  const [defaultPrefix, setDefaultPrefix] = useState('');
  const [prefixLoading, setPrefixLoading] = useState(false);
  const [migratingIds, setMigratingIds] = useState(false);
  const [doctorsCount, setDoctorsCount] = useState(null);

  useEffect(() => {
    if (profile) {
      // Admin always has password reset enabled by default
      if (profile.role === 'admin') {
        setCanReset(true);
      } else {
        setCanReset(profile.can_reset_password !== false);
      }
    }
  }, [profile]);

  useEffect(() => {
    // Set current logo URL when branding loads
    if (branding) {
      setLogoUrl(branding.logoUrl || '');
    }
  }, [branding]);

  useEffect(() => {
    // Load prefix settings from hospital data
    const loadPrefixSettings = async () => {
      if (!hospital?.id) return;
      
      try {
        const data = await apiRequest(`get_hospital_status.php?hospital_id=${hospital.id}`);
        console.log('Loaded hospital status:', data);
        if (data.success && data.hospital) {
          console.log('Setting prefix state:', {
            default_prefix: data.hospital.default_prefix,
            prefix_override_enabled: data.hospital.prefix_override_enabled,
            prefix_override: data.hospital.prefix_override
          });
          setDefaultPrefix(data.hospital.default_prefix || '');
          // Convert to boolean properly (handle 0/1 from database)
          const enabled = data.hospital.prefix_override_enabled === true || 
                         data.hospital.prefix_override_enabled === 1 || 
                         data.hospital.prefix_override_enabled === '1';
          setPrefixOverrideEnabled(enabled);
          // Handle prefix_override - if disabled, clear it; if enabled, use value or empty
          const overrideValue = data.hospital.prefix_override;
          if (!enabled) {
            setPrefixOverride('');
          } else {
            setPrefixOverride(overrideValue !== null && overrideValue !== undefined && overrideValue !== '' ? overrideValue : '');
          }
        }
      } catch (error) {
        console.error('Failed to load prefix settings:', error);
      }
    };
    
    loadPrefixSettings();
  }, [hospital]);

  // Check if there are any doctors to migrate
  const checkDoctorsCount = useCallback(async () => {
    if (!hospital?.id) return;
    
    try {
      const data = await apiRequest(`get_users.php?hospital_id=${hospital.id}`);
      if (data.success && data.users) {
        // Count doctors that match the migration criteria (same roles as migrate_doctor_ids.php)
        // The API checks for: 'doctor', 'chief_doctor', 'admin', 'clinic_login'
        const doctorsToMigrate = data.users.filter(user => {
          const role = (user.role || '').toLowerCase();
          return ['doctor', 'chief_doctor', 'admin', 'clinic_login'].includes(role);
        });
        setDoctorsCount(doctorsToMigrate.length);
      } else {
        setDoctorsCount(0);
      }
    } catch (error) {
      console.error('Failed to check doctors count:', error);
      setDoctorsCount(0);
    }
  }, [hospital?.id]);

  useEffect(() => {
    checkDoctorsCount();
  }, [checkDoctorsCount]);

  // Listen for doctors list refresh event to update count
  useEffect(() => {
    const handleDoctorsRefresh = () => {
      checkDoctorsCount();
    };
    
    window.addEventListener('doctorsListRefresh', handleDoctorsRefresh);
    
    return () => {
      window.removeEventListener('doctorsListRefresh', handleDoctorsRefresh);
    };
  }, [checkDoctorsCount]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Admin always has password reset enabled, skip check for admin
    if (!canReset && profile?.role !== 'admin') {
      toast({
        title: 'Password Reset Disabled',
        description: 'Your administrator has disabled password reset for your account.',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const data = await apiRequest('update_password.php', {
        method: 'POST',
        body: JSON.stringify({password: newPassword}),
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Your password has been updated successfully.',
          variant: 'default'
        });
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to update password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file (JPG, PNG, GIF, or WebP)',
          variant: 'destructive'
        });
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Logo file must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      setLogoFile(file);
      setLogoUrl(''); // Clear URL input when file is selected
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogoFile = () => {
    setLogoFile(null);
    setLogoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('hospital_logo_file');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUpdateLogo = async (e) => {
    e.preventDefault();
    
    // Validate that either file or URL is provided
    if (!logoFile && !logoUrl) {
      toast({
        title: 'Validation Error',
        description: 'Please select a logo file or enter a logo URL',
        variant: 'destructive'
      });
      return;
    }
    
    setLogoLoading(true);
    try {
      let data;
      
      // If file is selected, use FormData for file upload
      if (logoFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('hospital_logo', logoFile);
        
        // Get API base URL - use same logic as api.js
        const getApiBaseUrl = () => {
          if (import.meta.env.VITE_API_BASE_URL) {
            return import.meta.env.VITE_API_BASE_URL.replace(/\/+$/, '');
          }
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return 'http://localhost:8001/api';
          }
          // Production: Use /api/api path since files are in api/api/ folder
          return `${window.location.protocol}//${window.location.hostname}/api/api`;
        };
        const API_BASE_URL = getApiBaseUrl();
        const token = localStorage.getItem('hospital_session_token') || localStorage.getItem('session_token');
        
        const response = await fetch(`${API_BASE_URL}/hospital_update_logo.php`, {
          method: 'POST',
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {},
          body: formDataToSend
        });
        
        const text = await response.text();
        if (!text) {
          throw new Error('Empty response from server');
        }
        
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Server error: ${text.substring(0, 200)}`);
        }
      } else {
        // Use URL input (existing functionality)
        data = await apiRequest('hospital_update_logo.php', {
          method: 'PUT',
          body: JSON.stringify({ logo_url: logoUrl }),
        });
      }

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Hospital logo updated successfully.',
          variant: 'default'
        });
        // Clear file preview and file after successful upload
        setLogoFile(null);
        setLogoPreview(null);
        setLogoUrl('');
        // Refresh branding to get updated logo
        if (refreshBranding) {
          refreshBranding();
        }
        // Also refresh hospital status
        if (refreshHospitalStatus) {
          await refreshHospitalStatus();
        }
        // Also update localStorage
        if (data.hospital) {
          localStorage.setItem('hospital_data', JSON.stringify(data.hospital));
        }
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to update logo',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update logo',
        variant: 'destructive'
      });
    } finally {
      setLogoLoading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl('');
    setLogoLoading(true);
    try {
      const data = await apiRequest('hospital_update_logo.php', {
        method: 'PUT',
        body: JSON.stringify({ logo_url: '' }),
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Logo removed. General dental logo will be displayed.',
          variant: 'default'
        });
        if (refreshBranding) {
          refreshBranding();
        }
        if (data.hospital) {
          localStorage.setItem('hospital_data', JSON.stringify(data.hospital));
        }
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to remove logo',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove logo',
        variant: 'destructive'
      });
    } finally {
      setLogoLoading(false);
    }
  };

  const handleUpdatePrefix = async (e) => {
    e.preventDefault();
    
    // Validate prefix if override is enabled
    if (prefixOverrideEnabled) {
      const prefix = prefixOverride.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(prefix)) {
        toast({
          title: 'Error',
          description: 'Prefix must be exactly 3 uppercase letters (e.g., ABC)',
          variant: 'destructive'
        });
        return;
      }
    }
    
    setPrefixLoading(true);
    try {
      const requestBody = {
        prefix_override_enabled: prefixOverrideEnabled,
        prefix_override: prefixOverrideEnabled && prefixOverride.trim() ? prefixOverride.trim().toUpperCase() : null
      };
      
      console.log('Updating prefix settings:', requestBody);
      
      const data = await apiRequest('update_hospital_prefix.php', {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
      
      console.log('Update response:', data);
      console.log('Debug info:', data.debug);

      if (data.success) {
        // Update local state with response immediately
        if (data.hospital) {
          console.log('Updating state with response:', {
            default_prefix: data.hospital.default_prefix,
            prefix_override_enabled: data.hospital.prefix_override_enabled,
            prefix_override: data.hospital.prefix_override,
            raw_override: data.hospital.prefix_override,
            enabled_type: typeof data.hospital.prefix_override_enabled
          });
          
          // Update state immediately from API response
          setDefaultPrefix(data.hospital.default_prefix || '');
          
          // Convert to boolean - handle all possible formats
          let enabled = false;
          if (data.hospital.prefix_override_enabled === true) {
            enabled = true;
          } else if (data.hospital.prefix_override_enabled === 1 || data.hospital.prefix_override_enabled === '1') {
            enabled = true;
          } else if (data.hospital.prefix_override_enabled === false || data.hospital.prefix_override_enabled === 0 || data.hospital.prefix_override_enabled === '0') {
            enabled = false;
          }
          
          console.log('Setting enabled to:', enabled);
          // Force state update with explicit boolean
          setPrefixOverrideEnabled(Boolean(enabled));
          
          // Handle prefix_override - if disabled, clear it; if enabled but null, keep empty; if has value, use it
          const overrideValue = data.hospital.prefix_override;
          if (!enabled) {
            // Override is disabled, clear the value
            console.log('Override disabled - clearing prefixOverride');
            setPrefixOverride('');
          } else {
            // Override is enabled - use the value or empty string
            const finalValue = overrideValue && overrideValue !== null && overrideValue !== '' ? overrideValue : '';
            console.log('Override enabled - setting prefixOverride to:', finalValue);
            setPrefixOverride(finalValue);
          }
          
          console.log('State updated - enabled:', enabled, 'override:', overrideValue);
          
          // Force a re-render by updating a dummy state
          setPrefixLoading(prev => !prev);
        }
        
        toast({
          title: 'Success',
          description: 'Prefix settings updated successfully. New IDs will use the updated prefix.',
          variant: 'default'
        });
        
        // Refresh hospital status in context to update all components immediately
        if (refreshHospitalStatus) {
          refreshHospitalStatus();
        }
        
        // Dispatch custom event to notify other components (like AdminDoctorsTab)
        window.dispatchEvent(new CustomEvent('hospitalPrefixUpdated'));
        
        // Force refresh after a short delay to ensure database is updated
        setTimeout(async () => {
          try {
            const hospitalData = await apiRequest(`get_hospital_status.php?hospital_id=${hospital?.id}&_t=${Date.now()}`);
            console.log('Refreshed hospital data after update:', hospitalData);
            if (hospitalData.success && hospitalData.hospital) {
              console.log('Updating state from refresh:', {
                default_prefix: hospitalData.hospital.default_prefix,
                prefix_override_enabled: hospitalData.hospital.prefix_override_enabled,
                prefix_override: hospitalData.hospital.prefix_override
              });
              setDefaultPrefix(hospitalData.hospital.default_prefix || '');
              
              // Convert to boolean - handle all possible formats
              let enabled = false;
              if (hospitalData.hospital.prefix_override_enabled === true) {
                enabled = true;
              } else if (hospitalData.hospital.prefix_override_enabled === 1 || hospitalData.hospital.prefix_override_enabled === '1') {
                enabled = true;
              } else if (hospitalData.hospital.prefix_override_enabled === false || hospitalData.hospital.prefix_override_enabled === 0 || hospitalData.hospital.prefix_override_enabled === '0') {
                enabled = false;
              }
              
              console.log('Setting enabled from refresh to:', enabled);
              // Force state update with explicit boolean
              setPrefixOverrideEnabled(Boolean(enabled));
              
              // Handle prefix_override based on enabled state
              const overrideValue = hospitalData.hospital.prefix_override;
              if (!enabled) {
                // Override is disabled - clear the value
                console.log('Override disabled from refresh - clearing prefixOverride');
                setPrefixOverride('');
              } else {
                // Override is enabled - use the value or empty string
                const finalValue = overrideValue !== null && overrideValue !== undefined && overrideValue !== '' ? overrideValue : '';
                console.log('Override enabled from refresh - setting prefixOverride to:', finalValue);
                setPrefixOverride(finalValue);
              }
              console.log('State updated from refresh - enabled:', enabled, 'override:', overrideValue);
              
              // Force a re-render by updating a dummy state
              setPrefixLoading(prev => !prev);
            }
          } catch (refreshError) {
            console.error('Failed to refresh hospital data:', refreshError);
          }
        }, 300);
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to update prefix settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update prefix settings',
        variant: 'destructive'
      });
    } finally {
      setPrefixLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">System Settings</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hospital Logo Card */}
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Image className="h-5 w-5 mr-2 text-blue-400" />
              Hospital Logo
            </CardTitle>
            <CardDescription className="text-gray-300">
              Upload or set a URL for your hospital logo. Leave empty to use the general dental logo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateLogo} className="space-y-4">
              {/* File Upload Section */}
              <div>
                <Label htmlFor="hospital_logo_file" className="text-white">Upload Logo</Label>
                <div className="space-y-2">
                  {logoPreview ? (
                    <div className="relative">
                      <div className="relative border-2 border-gray-600 rounded-lg p-2 bg-gray-800">
                        <img 
                          src={logoPreview} 
                          alt="Logo preview" 
                          className="w-full h-32 object-contain rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveLogoFile}
                          className="absolute top-2 right-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 bg-gray-800/50 hover:border-gray-500 transition-colors">
                      <label htmlFor="hospital_logo_file" className="cursor-pointer block">
                        <div className="flex flex-col items-center space-y-2">
                          <Upload className="h-8 w-8 text-gray-400" />
                          <div className="text-sm text-gray-300">
                            <span className="text-blue-400 hover:text-blue-300">Click to upload</span> or drag and drop
                          </div>
                          <p className="text-xs text-gray-400">JPG, PNG, GIF or WebP (MAX. 5MB)</p>
                        </div>
                      </label>
                      <input
                        id="hospital_logo_file"
                        name="hospital_logo"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* URL Input (Alternative) */}
              <div>
                <Label htmlFor="logo-url" className="text-white">Or Enter Logo URL</Label>
                <Input
                  id="logo-url"
                  type="url"
                  value={logoUrl}
                  onChange={(e) => {
                    setLogoUrl(e.target.value);
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="https://example.com/logo.png"
                  disabled={!!logoFile}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Enter a direct URL to your logo image (disabled when file is selected)
                </p>
              </div>
              
              {/* Current Logo Display */}
              {branding?.logoUrl && !logoPreview && !logoFile && (
                <div>
                  <Label className="text-white">Current Logo</Label>
                  <div className="flex items-center gap-3 mt-2 p-4 bg-gray-800/50 rounded-lg">
                    <div className="flex-shrink-0">
                      <img 
                        src={branding.logoUrl} 
                        alt="Current Logo" 
                        className="h-16 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 truncate">{branding.logoUrl}</p>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleRemoveLogo}
                      disabled={logoLoading}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  type="submit"
                  className="flex-1 medical-gradient text-white hover:opacity-90"
                  disabled={logoLoading || (!logoFile && !logoUrl)}
                >
                  {logoLoading ? (
                    <>
                      <Upload className="h-4 w-4 mr-2 animate-spin" />
                      {logoFile ? 'Uploading...' : 'Updating...'}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {logoFile ? 'Upload Logo' : (logoUrl ? 'Update Logo' : 'Update Logo')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Key className="h-5 w-5 mr-2 text-blue-400" />
              Change Password
            </CardTitle>
            <CardDescription className="text-gray-300">
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!canReset && profile?.role !== 'admin' ? (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">
                  Password reset is disabled for your account. Please contact your administrator.
                </p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-white">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white pr-10"
                      placeholder="Enter new password"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-white">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white pr-10"
                      placeholder="Confirm new password"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* ID Prefix Settings Card */}
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Hash className="h-5 w-5 mr-2 text-blue-400" />
              ID Prefix Settings
            </CardTitle>
            <CardDescription className="text-gray-300">
              Configure the prefix used for auto-generated IDs (doctors, staff, patients)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePrefix} className="space-y-4">
              {/* Default Prefix (Read-only) */}
              <div>
                <Label htmlFor="default-prefix" className="text-white">Default Prefix</Label>
                <Input
                  id="default-prefix"
                  value={defaultPrefix || 'Not set'}
                  className="bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-400 mt-1">
                  Auto-generated from hospital name. This cannot be changed.
                </p>
              </div>

              {/* Override Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="prefix-override-enabled"
                  checked={!!prefixOverrideEnabled}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    console.log('Checkbox onChange - new value:', newValue);
                    setPrefixOverrideEnabled(newValue);
                    if (!newValue) {
                      setPrefixOverride('');
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <Label htmlFor="prefix-override-enabled" className="text-white cursor-pointer">
                  Overwrite default prefix
                </Label>
              </div>

              {/* Override Input */}
              {prefixOverrideEnabled && (
                <div>
                  <Label htmlFor="prefix-override" className="text-white">Custom Prefix</Label>
                  <Input
                    id="prefix-override"
                    value={prefixOverride}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
                      setPrefixOverride(value);
                    }}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="ABC"
                    maxLength={3}
                    pattern="[A-Z]{3}"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Enter exactly 3 uppercase letters. This will be used for all new IDs.
                  </p>
                </div>
              )}

              {/* Current Effective Prefix Display */}
              <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-600">
                <p className="text-xs text-gray-400 mb-1">Current Effective Prefix:</p>
                <p className="text-lg font-semibold text-white">
                  {prefixOverrideEnabled && prefixOverride ? prefixOverride : (defaultPrefix || 'N/A')}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Example IDs: {prefixOverrideEnabled && prefixOverride ? prefixOverride : (defaultPrefix || 'XXX')}-D-001, {prefixOverrideEnabled && prefixOverride ? prefixOverride : (defaultPrefix || 'XXX')}-S-001, {prefixOverrideEnabled && prefixOverride ? prefixOverride : (defaultPrefix || 'XXX')}-P-0001
                </p>
              </div>

              {/* Migrate Existing Doctor IDs - Only show if there are doctors */}
              {doctorsCount !== null && doctorsCount > 0 && (
                <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-700/50">
                  <p className="text-sm text-yellow-200 mb-2 font-medium">
                    Update Existing Doctor IDs
                  </p>
                  <p className="text-xs text-gray-400 mb-3">
                    This will update all existing doctor IDs ({doctorsCount} {doctorsCount === 1 ? 'doctor' : 'doctors'}) in the Doctors tab to use the current effective prefix ({prefixOverrideEnabled && prefixOverride ? prefixOverride : (defaultPrefix || 'GDC')}). 
                    This action cannot be undone.
                  </p>
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!confirm(`Are you sure you want to update all ${doctorsCount} existing doctor ID${doctorsCount === 1 ? '' : 's'}? This action cannot be undone.`)) {
                        return;
                      }
                      setMigratingIds(true);
                      try {
                        console.log('Calling migrate_doctor_ids.php...');
                        const data = await apiRequest('migrate_doctor_ids.php', {
                          method: 'POST'
                        });
                        console.log('Migration response:', data);
                        if (data.success) {
                          toast({
                            title: 'Success',
                            description: data.message || `Successfully updated ${data.updated || 0} doctor ID${(data.updated || 0) === 1 ? '' : 's'}`,
                            variant: 'default'
                          });
                          // Update doctors count after migration
                          setDoctorsCount(0); // All migrated, so no more to migrate
                          // Dispatch event to refresh doctors list
                          window.dispatchEvent(new CustomEvent('doctorsListRefresh'));
                          // Also refresh hospital status
                          if (refreshHospitalStatus) {
                            await refreshHospitalStatus();
                          }
                          // Dispatch prefix update event
                          window.dispatchEvent(new CustomEvent('hospitalPrefixUpdated'));
                        } else {
                          console.error('Migration failed:', data);
                          toast({
                            title: 'Error',
                            description: data.error || data.message || 'Failed to migrate doctor IDs',
                            variant: 'destructive'
                          });
                        }
                      } catch (error) {
                        console.error('Migration error:', error);
                        toast({
                          title: 'Error',
                          description: error.message || 'Failed to migrate doctor IDs',
                          variant: 'destructive'
                        });
                      } finally {
                        setMigratingIds(false);
                      }
                    }}
                    disabled={migratingIds || prefixLoading}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    {migratingIds ? 'Migrating...' : `Update Existing Doctor IDs (${doctorsCount})`}
                  </Button>
                </div>
              )}

              <Button
                type="submit"
                className="w-full medical-gradient text-white hover:opacity-90"
                disabled={prefixLoading || (prefixOverrideEnabled && prefixOverride.length !== 3)}
              >
                {prefixLoading ? 'Updating...' : 'Update Prefix Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettingsTab;
